import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type {
  ScheduleSyncedEventProps,
  ScheduleUploadCompletedEventProps,
  ScheduleUploadProcessingFailedEventProps,
  ScheduleUploadPromoClickedEventProps,
  ScheduleUploadPromoViewedEventProps,
  ScheduleUploadSessionsCreatedEventProps,
  ScheduleViewedEventProps,
} from '@heidi/session/contracts';

/**
 * App-side emit hosts for the schedule funnel (`@heidi/session`).
 *
 * ROUTING: every one passes `{ amplitude: false }`, which is what the Rematch
 * wrappers they replace passed. The lifecycle host next door is NOT uniform
 * with them — read that file before "tidying" either.
 *
 * The enum member, not `scheduleViewed.name`: `event_name` discriminates
 * `TrackingEventV2` on string ENUM members. The suite asserts they agree.
 *
 * The props objects are rebuilt field by field rather than spread, because the
 * wrappers were: a spread would start carrying whatever a caller passes.
 */

export const trackScheduleViewed = (props: ScheduleViewedEventProps): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ScheduleViewed,
    props: { date: props.date, ehr_provider: props.ehr_provider },
  });
};

export const trackScheduleSynced = (props: ScheduleSyncedEventProps): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ScheduleSynced,
    props: { ehr_provider: props.ehr_provider },
  });
};

export const trackScheduleUploadCompleted = (
  props: ScheduleUploadCompletedEventProps
): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ScheduleUploadCompleted,
    props: {
      num_processed: props.num_processed,
      processing_duration_ms: props.processing_duration_ms,
    },
  });
};

export const trackScheduleUploadProcessingFailed = (
  props: ScheduleUploadProcessingFailedEventProps
): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ScheduleUploadProcessingFailed,
    props: { error_message: props.error_message, error_code: props.error_code },
  });
};

export const trackScheduleUploadSessionsCreated = (
  props: ScheduleUploadSessionsCreatedEventProps
): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ScheduleUploadSessionsCreated,
    props: { num_created: props.num_created, num_skipped: props.num_skipped },
  });
};

export const trackScheduleUploadPromoViewed = (
  props: ScheduleUploadPromoViewedEventProps
): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ScheduleUploadPromoViewed,
    props: { display: props.display },
  });
};

export const trackScheduleUploadPromoClicked = (
  props: ScheduleUploadPromoClickedEventProps
): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.ScheduleUploadPromoClicked,
    props: { display: props.display },
  });
};
